"use client";

// Desktop primary navigation. Renders the NAV structure as a row of links
// and dropdown groups. Groups open on hover and on keyboard focus; the
// chevron is a real disclosure button with aria-expanded. Escape and an
// outside click close an open menu.

import { useEffect, useId, useRef, useState } from "react";
import Link from "next/link";
import { Icon } from "@/components/ui/icon";
import { NAV, isGroup, type NavGroup } from "@/components/nav-items";

const TRIGGER =
  "font-label-caps text-label-caps text-on-surface-variant no-underline transition-colors hover:text-primary";

export function MainNav() {
  return (
    <nav aria-label="Primary" className="hidden items-center gap-6 md:flex">
      {NAV.map((node) =>
        isGroup(node) ? (
          <NavDropdown key={node.label} group={node} />
        ) : (
          <Link key={node.label} href={node.href} className={TRIGGER}>
            {node.label}
          </Link>
        ),
      )}
    </nav>
  );
}

function NavDropdown({ group }: { group: NavGroup }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    function onDocDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => {
        // Keep the menu open if a keyboard user is focused inside it.
        if (!wrapRef.current?.contains(document.activeElement)) setOpen(false);
      }}
      onBlur={(e) => {
        if (!wrapRef.current?.contains(e.relatedTarget as Node)) setOpen(false);
      }}
    >
      <div className="flex items-center">
        {group.href ? (
          <Link href={group.href} className={TRIGGER} onClick={() => setOpen(false)}>
            {group.label}
          </Link>
        ) : (
          <button
            type="button"
            className={`${TRIGGER} cursor-pointer`}
            aria-haspopup="true"
            aria-expanded={open}
            aria-controls={menuId}
            onClick={() => setOpen((o) => !o)}
            onFocus={() => setOpen(true)}
          >
            {group.label}
          </button>
        )}
        <button
          type="button"
          aria-haspopup="true"
          aria-expanded={open}
          aria-controls={menuId}
          aria-label={`${group.label} menu`}
          onClick={() => setOpen((o) => !o)}
          className="ml-1 flex h-6 w-6 items-center justify-center rounded-full text-on-surface-variant transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <Icon
            name="expand_more"
            size={18}
            className={`transition-transform duration-150 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {open && (
        <div className="absolute left-0 top-full z-50 pt-2">
          <ul
            id={menuId}
            className="min-w-[15rem] rounded-xl border border-outline-variant bg-surface-container-lowest p-1.5 shadow-lg"
            style={{ animation: "popover-in 120ms ease-out" }}
          >
            {group.children.map((child) => (
              <li key={child.href}>
                <Link
                  href={child.href}
                  onClick={() => setOpen(false)}
                  className="block rounded-lg px-3 py-2 font-body-md text-body-md text-on-surface no-underline transition-colors hover:bg-surface-container"
                >
                  {child.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
